import { compactClassName } from '@/components/homepage-october/compact'

import { DestinationLink } from './DestinationLink'
import * as React from 'react'

import { Button } from '@/components/homepage-october/ui/button'
import { cn } from '@/lib/utils'
import { ArrowRight } from './icons'
import type { ChipRow, Head, Link } from '../types'

/** Strip a trailing arrow glyph; v3 draws the arrow as an icon so screen readers hear only the words. */
export const words = (s: string) => s.replace(/\s*[→›]\s*$/, '').replace(/^\s*[←]\s*/, '')

export function Container({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={compactClassName(cn('mx-auto w-full max-w-[2400px] px-4 sm:px-6 lg:px-8 2xl:px-10', className))} {...props} />
}

/** Light section tints. Each department keeps its own colour cue, on light backgrounds only. */
export const TONES = {
  white: 'bg-white',
  mist: 'bg-neutral-50',
  sky: 'bg-[#f3f8ff]',
  peach: 'bg-[#fff7f0]',
  // The four below kept their names so section files need no change; they now map onto the brand tints above.
  mint: 'bg-[#f3f8ff]',
  lilac: 'bg-[#f3f8ff]',
  sand: 'bg-[#fff7f0]',
  ice: 'bg-neutral-50',
} as const
export type Tone = keyof typeof TONES

/** Dot colours that pair with each tint (eyebrows, accents). */
export const ACCENT: Record<Tone, string> = {
  white: 'bg-[#ff9951]',
  mist: 'bg-[#ff9951]',
  sky: 'bg-[#0469f0]',
  peach: 'bg-[#ff9951]',
  mint: 'bg-[#10a65c]',
  lilac: 'bg-[#0469f0]',
  sand: 'bg-[#b54708]',
  ice: 'bg-[#0891b2]',
}

export function Section({ tone = 'white', id, aria, v2, className, children, bleed }: {
  tone?: Tone
  id?: string
  aria: string
  v2: string
  className?: string
  children: React.ReactNode
  bleed?: boolean
}) {
  return (
    <section id={id} aria-label={aria} data-v3={v2} className={compactClassName(cn('relative scroll-mt-28 py-10 md:py-14', TONES[tone], className))}>
      {bleed ? children : <Container>{children}</Container>}
    </section>
  )
}

export function Eyebrow({ children, tone = 'white', className }: { children: React.ReactNode; tone?: Tone; className?: string }) {
  return (
    <p className={compactClassName(cn('inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#0454c0]', className))}>
      <span aria-hidden="true" className={compactClassName(cn('size-1.5 rounded-full', ACCENT[tone]))} />
      {children}
    </p>
  )
}

export function ViewAll({ link, className, variant = 'outline' }: { link: Link; className?: string; variant?: 'outline' | 'ghost' | 'link' }) {
  return (
    <Button asChild variant={variant} className={cn('min-w-0 max-w-full shrink-0 whitespace-normal! rounded-full! text-left', variant === 'link' && 'h-auto! px-0! text-[#0454c0]!', className)}>
      <DestinationLink href={link.href} aria-label={link.aria} id={link.id}>
        {words(link.text)}
        <ArrowRight aria-hidden="true" />
      </DestinationLink>
    </Button>
  )
}

/** Section heading row: eyebrow, h2, sub line on the left; the view-all link on the right. */
export function SectionHeader({ head, tone = 'white', level = 2, className, children }: {
  head: Head
  tone?: Tone
  level?: 2 | 3
  className?: string
  children?: React.ReactNode
}) {
  const H = level === 2 ? 'h2' : 'h3'
  return (
    <div className={compactClassName(cn('mb-6 flex flex-col gap-4 md:mb-8 md:flex-row md:items-end md:justify-between', className))}>
      <div className={compactClassName("max-w-3xl")}>
        {head.eyebrow ? <Eyebrow tone={tone}>{head.eyebrow}</Eyebrow> : null}
        <H id={head.id} className={compactClassName(cn('text-balance font-bold tracking-tight text-neutral-950', head.eyebrow && 'mt-3', level === 2 ? 'text-3xl md:text-[2.5rem] md:leading-[1.1]' : 'text-2xl'))}>
          {head.title}
        </H>
        {head.sub ? (
          <p id={head.subId} className={compactClassName("mt-3 max-w-2xl text-pretty text-base leading-relaxed text-neutral-600 md:text-lg")}>
            {head.sub}
          </p>
        ) : null}
      </div>
      {children}
      {head.viewAll ? <ViewAll link={head.viewAll} /> : null}
    </div>
  )
}

/** Small heading row for a block inside a section. */
export function SubHeader({ title, link, sub, as = 'h3', className, id }: { title: string; link?: Link | null; sub?: string; as?: 'h2' | 'h3'; className?: string; id?: string }) {
  const H = as
  return (
    <div className={compactClassName(cn('mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-2', className))}>
      <div className={compactClassName("min-w-0")}>
        <H id={id} className={compactClassName("text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>{title}</H>
        {sub ? <p className={compactClassName("mt-1.5 max-w-2xl text-sm leading-relaxed text-neutral-600 md:text-base")}>{sub}</p> : null}
      </div>
      {link ? <ViewAll link={link} variant="link" /> : null}
    </div>
  )
}

/** Pill links. Same destinations as v2's chips, drawn as shadcn outline buttons. */
/** Chip links: the outline Button's styles, one class (v3-chip in v3/card.css) for all 176 on the page.
 *  `drift` lets each chip wander a few pixels on its own clock (the "Also searched" cloud); transform only, and
 *  [data-motion] holds still when the device asks for reduced motion. */
export function ChipLinks({ links, className, drift }: { links: Link[]; className?: string; drift?: boolean }) {
  return (
    <ul className={compactClassName(cn('flex flex-wrap gap-1.5', className))}>
      {links.map((l, i) => (
        <li key={l.href + l.text} data-motion={drift ? '' : undefined} style={drift ? { animation: `v3-drift ${(7 + (i * 37) % 50 / 10).toFixed(1)}s ease-in-out ${(-(i * 53) % 70 / 10).toFixed(1)}s infinite` } : undefined}>
          <DestinationLink href={l.href} aria-label={l.aria} className="v3-chip">{words(l.text)}</DestinationLink>
        </li>
      ))}
    </ul>
  )
}

export function ChipRows({ rows, className }: { rows: ChipRow[]; className?: string }) {
  if (!rows.length) return null
  return (
    <div className={compactClassName(cn('mt-6 grid gap-2.5', className))}>
      {rows.map((r) => (
        <div key={r.label} className={compactClassName("flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3")}>
          <p className={compactClassName("shrink-0 pt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-600 sm:w-28")}>{r.label}</p>
          <ChipLinks links={r.links} />
        </div>
      ))}
    </div>
  )
}

/** A pulsing green "live" dot. */
export function LiveDot({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={compactClassName(cn('relative inline-flex size-2', className))}>
      <span data-motion className={compactClassName("absolute inline-flex size-full animate-v3-ping rounded-full bg-[#10a65c] opacity-60")} />
      <span className={compactClassName("relative inline-flex size-2 rounded-full bg-[#10a65c]")} />
    </span>
  )
}

/** "Prices checked today, 10/14/2026" style stamp. */
export function Stamp({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={compactClassName(cn('inline-flex items-center gap-2 rounded-full border border-[#10a65c]/25 bg-[#f0faf4] px-3 py-1 text-xs font-medium text-[#05603a]', className))}>
      <LiveDot />
      {children}
    </p>
  )
}

/** FIN identifier chip, monospace like the live cards. */
export function IdChip({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={compactClassName(cn('v3-id', className))}>{children}</span>
}
